import { useState, useEffect, useCallback, useRef } from "react";
import {
  getCargoFlightSchedule, fixCargoFlight, updateCargoFlight,
  setCargoFlightEntries, deleteCargoFlight, listCargoEntries,
  getCargoFlightAssignments, listCargoFlights, enrichCargoFlights,
  getFlightCargo, releaseFlightCargo, splitFlightCargo, importCargoFlightInfo,
  getUpcomingCargoFlights, generateCargoManifest, generateCargoTags,
  saveCargoManifestData, saveCargoTagsData, updateCargoEntry,
} from "@/lib/api";
import { useRefreshOnFocus } from "@/hooks/useRefreshOnFocus";
import { DirectionBadge as DirBadge } from "@/components/DirectionBadge";
import {
  Plane, Search, Plus, Pin, Package,
  Pencil, Trash2, RefreshCw, X, AlertTriangle, Check, Tags, History, GitBranch,
  Split, Unlink, ArrowLeft, Upload, ArrowDownUp, CalendarClock, FileText, Save,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const StatusChip = ({ status }) => {
  if (!status) return <span className="text-slate-300">—</span>;
  const s = status.toUpperCase();
  let cls = "bg-slate-100 text-slate-600";
  if (/LAND|ARRIV/.test(s)) cls = "bg-emerald-100 text-emerald-700";
  else if (/BOARD|DEPART/.test(s)) cls = "bg-amber-100 text-amber-700";
  else if (/CLOSED|CANCEL/.test(s)) cls = "bg-rose-100 text-rose-700";
  else if (/APPROCHE|APPROACH|CHECKIN/.test(s)) cls = "bg-sky-100 text-sky-700";
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{status}</span>;
};

const emptyManual = {
  flight_number: "", registration: "", aircraft_type: "",
  origin: "", destination: "", direction: "departure",
  scheduled_time: "", airline: "", city: "",
};

export default function CargoInFlightPage() {
  const [date, setDate] = useState(todayISO());
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scrapeOk, setScrapeOk] = useState(true);
  const [source, setSource] = useState("airport");
  const [entries, setEntries] = useState([]);

  const [searchOpen, setSearchOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [editFlight, setEditFlight] = useState(null);
  const [cargoFlight, setCargoFlight] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [pastFlights, setPastFlights] = useState([]);
  const [pastSort, setPastSort] = useState("desc"); // desc = newest→oldest (default)
  const loadPast = useCallback(() => {
    listCargoFlights().then((r) => {
      const t = todayISO();
      const list = (r.data || []).filter((f) => (f.flight_date || "") < t);
      setPastFlights(list);
    }).catch(() => {});
  }, []);

  const [futureFlights, setFutureFlights] = useState([]);
  const [futureLoading, setFutureLoading] = useState(false);
  const [futureSort, setFutureSort] = useState("desc");
  const loadFuture = useCallback(() => {
    setFutureLoading(true);
    getUpcomingCargoFlights({ days: 3 }).then((r) => {
      setFutureFlights(r.data?.flights || []);
    }).catch(() => {}).finally(() => setFutureLoading(false));
  }, []);

  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await importCargoFlightInfo(fd);
      toast.success(`Updated ${data.updated} flight(s) from Excel${data.unmatched ? ` · ${data.unmatched} rows unmatched` : ""}`);
      load();
      loadPast();
      loadFuture();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not import flight info");
    } finally {
      setImporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCargoFlightSchedule({ date, scope: "pegasus" });
      setFlights(data.flights || []);
      setScrapeOk(data.scrape_ok);
      setSource(data.source || null);
    } catch (e) {
      toast.error("Failed to load flights");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { listCargoEntries().then((r) => setEntries(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { loadPast(); }, [loadPast]);
  useEffect(() => { loadFuture(); }, [loadFuture]);
  useRefreshOnFocus(() => {
    load();
    loadPast();
    loadFuture();
    listCargoEntries().then((r) => setEntries(r.data || [])).catch(() => {});
  });

  const sortByDate = (list, dir) =>
    [...list].sort((a, b) => {
      const c = (a.flight_date || "").localeCompare(b.flight_date || "")
        || (a.scheduled_time || "").localeCompare(b.scheduled_time || "");
      return dir === "asc" ? c : -c;
    });
  const pastSorted = sortByDate(pastFlights, pastSort);
  const futureFixed = futureFlights.filter((f) => f.fixed);
  const futureSorted = sortByDate(futureFixed, futureSort);

  // Live status auto-refresh every 3 minutes when viewing today.
  useEffect(() => {
    if (date !== todayISO()) return undefined;
    const t = setInterval(() => { load(); }, 180000);
    return () => clearInterval(t);
  }, [date, load]);

  const doFix = async (f) => {
    try {
      await fixCargoFlight({ ...f, flight_date: date, source: f.source || "airport" });
      toast.success(`Flight ${f.flight_number} fixed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fix flight");
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCargoFlight(deleteTarget.id);
      toast.success(`Flight ${deleteTarget.flight_number} removed`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Could not remove flight");
    }
  };

  const pegasusCount = flights.filter((f) => (f.flight_number || "").toUpperCase().startsWith("PC")).length;
  const fixedCount = flights.filter((f) => f.fixed).length;
  const withCargo = flights.filter((f) => (f.cargo_count || 0) > 0).length;

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="cargo-inflight-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Plane className="text-emerald-600" size={26} /> In Flight
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Today's Pegasus (PC) cargo flights to/from Istanbul Sabiha (SAW), live from Charleroi Airport.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-auto"
            data-testid="inflight-date-picker"
          />
          <Button variant="outline" onClick={load} disabled={loading} data-testid="inflight-refresh-btn">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </Button>
          <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="inflight-history-btn">
            <History size={16} className="mr-1.5" /> History
          </Button>
          <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={onImportFile} data-testid="inflight-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="inflight-import-btn" title="Fill registration / aircraft type / times from a movement-log Excel">
            <Upload size={16} className={`mr-1.5 ${importing ? "animate-pulse" : ""}`} /> Import info
          </Button>
          <Button variant="outline" onClick={() => setSearchOpen(true)} data-testid="inflight-search-btn">
            <Search size={16} className="mr-1.5" /> Search flight
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setManualOpen(true)} data-testid="inflight-manual-btn">
            <Plus size={16} className="mr-1.5" /> Manual entry
          </Button>
        </div>
      </div>

      {/* Stat pills */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 shadow-sm">
          <span className="text-xs text-slate-500">PC flights</span>
          <p className="text-lg font-bold text-slate-900" data-testid="inflight-stat-total">{pegasusCount}</p>
        </div>
        <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 shadow-sm">
          <span className="text-xs text-slate-500">Selected</span>
          <p className="text-lg font-bold text-emerald-600" data-testid="inflight-stat-fixed">{fixedCount}</p>
        </div>
        <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 shadow-sm">
          <span className="text-xs text-slate-500">With cargo</span>
          <p className="text-lg font-bold text-indigo-600" data-testid="inflight-stat-cargo">{withCargo}</p>
        </div>
      </div>

      {source === "fr24-live" && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 text-sm text-sky-800" data-testid="inflight-fr24-notice">
          <AlertTriangle size={16} />
          Airport live schedule is temporarily offline — showing live FlightRadar24 positions (currently airborne flights only). Use Manual entry or Import info for anything not shown.
        </div>
      )}

      {!scrapeOk && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="inflight-scrape-warning">
          <AlertTriangle size={16} />
          The live airport schedule source is temporarily offline. Fixed flights are still shown — use Manual entry or Import info, or retry shortly.
        </div>
      )}

      {/* Table (desktop) */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="inflight-table">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <th className="px-3 py-3">Dir</th>
                <th className="px-3 py-3">Date</th>
                <th className="px-3 py-3">Flight</th>
                <th className="px-3 py-3">Route</th>
                <th className="px-3 py-3">Sched</th>
                <th className="px-3 py-3">Est</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Reg</th>
                <th className="px-3 py-3">A/C</th>
                <th className="px-3 py-3">Cargo</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400">Loading live schedule…</td></tr>
              )}
              {!loading && flights.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400" data-testid="inflight-empty">
                  {scrapeOk ? "No Pegasus flights found for this date. Try Search flight or Manual entry." : "Live schedule source is offline — no flights to show. Use Manual entry or Import info."}
                </td></tr>
              )}
              {!loading && flights.map((f, i) => (
                <tr
                  key={f.id || `${f.flight_number}-${i}`}
                  className={`border-t border-slate-100 hover:bg-slate-50 ${f.fixed ? "bg-emerald-50/40" : ""}`}
                  data-testid={`inflight-row-${f.flight_number}`}
                >
                  <td className="px-3 py-2.5"><DirBadge dir={f.direction} /></td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600 font-medium">{f.flight_date || date}</td>
                  <td className="px-3 py-2.5">
                    <div className="font-bold text-slate-900 flex items-center gap-1.5">
                      {f.flight_number}
                      {f.fixed && <Pin size={13} className="text-emerald-600" title="Fixed" />}
                    </div>
                    <div className="text-xs text-slate-400">{f.airline}</div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className="font-medium text-slate-700">{f.origin || "—"}</span>
                    <span className="mx-1 text-slate-300">→</span>
                    <span className="font-medium text-slate-700">{f.destination || "—"}</span>
                  </td>
                  <td className="px-3 py-2.5 font-semibold text-slate-800">{f.scheduled_time || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-500">{f.estimated_time || "—"}</td>
                  <td className="px-3 py-2.5"><StatusChip status={f.status} /></td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{f.registration || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{f.aircraft_type || "—"}</td>
                  <td className="px-3 py-2.5">
                    {(f.cargo_count || 0) > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold w-fit">
                          <Package size={12} /> {f.cargo_count}
                        </span>
                        <span className="text-[11px] text-slate-500 whitespace-nowrap">
                          {Math.round(f.cargo_weight || 0).toLocaleString()} kg · {f.cargo_pieces || 0} pcs
                        </span>
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {!f.fixed ? (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => doFix(f)} data-testid={`inflight-fix-${f.flight_number}`}>
                          <Pin size={14} className="mr-1" /> Select
                        </Button>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setCargoFlight(f)} data-testid={`inflight-cargo-${f.flight_number}`}>
                            <Package size={14} className="mr-1" /> Cargo
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditFlight(f)} data-testid={`inflight-edit-${f.flight_number}`}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => setDeleteTarget(f)} data-testid={`inflight-delete-${f.flight_number}`}>
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards — today's flights */}
      <div className="md:hidden space-y-2" data-testid="inflight-cards">
        {loading && <p className="text-sm text-slate-400 py-6 text-center">Loading live schedule…</p>}
        {!loading && flights.length === 0 && <p className="text-sm text-slate-400 py-6 text-center" data-testid="inflight-empty-m">{scrapeOk ? "No Pegasus flights for this date. Try Search flight or Manual entry." : "Live schedule source is offline. Use Manual entry or Import info."}</p>}
        {!loading && flights.map((f, i) => (
          <div key={f.id || `${f.flight_number}-mc-${i}`} className={`rounded-xl border p-3 shadow-sm ${f.fixed ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`} data-testid={`inflight-card-${f.flight_number}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <DirBadge dir={f.direction} />
                <span className="font-bold text-slate-900">{f.flight_number}</span>
                {f.fixed && <Pin size={12} className="text-emerald-600" />}
              </div>
              <div className="flex items-center gap-1.5">
                <StatusChip status={f.status} />
                <span className="text-xs text-slate-500 whitespace-nowrap">{f.flight_date || date}</span>
              </div>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5 text-sm">
              <span className="font-medium text-slate-700">{f.origin || "—"}</span>
              <span className="text-slate-300">→</span>
              <span className="font-medium text-slate-700">{f.destination || "—"}</span>
              <span className="text-slate-400 text-xs ml-auto truncate">{f.airline}</span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <div><p className="text-slate-400">Sched</p><p className="font-semibold text-slate-800">{f.scheduled_time || "—"}</p></div>
              <div><p className="text-slate-400">Est</p><p className="text-slate-700">{f.estimated_time || "—"}</p></div>
              <div><p className="text-slate-400">Reg</p><p className="font-mono text-slate-700">{f.registration || "—"}</p></div>
            </div>
            {(f.cargo_count || 0) > 0 && (
              <div className="mt-2 flex items-center gap-1.5 text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold"><Package size={12} /> {f.cargo_count}</span>
                <span className="text-slate-500">{Math.round(f.cargo_weight || 0).toLocaleString()} kg · {f.cargo_pieces || 0} pcs</span>
              </div>
            )}
            <div className="mt-2.5 flex items-center gap-2">
              {!f.fixed ? (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-9 flex-1" onClick={() => doFix(f)} data-testid={`inflight-fix-${f.flight_number}-m`}><Pin size={14} className="mr-1" /> Select</Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" className="h-9 flex-1" onClick={() => setCargoFlight(f)} data-testid={`inflight-cargo-${f.flight_number}-m`}><Package size={14} className="mr-1" /> Cargo</Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setEditFlight(f)} data-testid={`inflight-edit-${f.flight_number}-m`}><Pencil size={15} /></Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9 text-rose-600" onClick={() => setDeleteTarget(f)} data-testid={`inflight-delete-${f.flight_number}-m`}><Trash2 size={15} /></Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400 mt-3">
        Status auto-refreshes every few minutes from the live airport schedule. At 23:59 (Brussels time) any fixed flight with no cargo booked is automatically removed. Unfixed flights are never saved.
      </p>

      {/* Future flights — upcoming PC↔SAW flights (live scrape + saved future flights) */}
      <div className="mt-8" data-testid="future-flights-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base md:text-lg font-semibold text-slate-800 flex items-center gap-2">
            <CalendarClock size={18} className="text-sky-600" /> Future flights
            <span className="text-sm font-normal text-slate-400">({futureFixed.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8" onClick={() => setFutureSort((s) => (s === "asc" ? "desc" : "asc"))} data-testid="future-sort-toggle">
              <ArrowDownUp size={14} className="mr-1.5" /> {futureSort === "asc" ? "Oldest → Newest" : "Newest → Oldest"}
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={loadFuture} disabled={futureLoading} data-testid="future-refresh-btn">
              <RefreshCw size={14} className={futureLoading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>
        {futureLoading ? (
          <p className="text-sm text-slate-400">Loading upcoming schedule…</p>
        ) : futureFixed.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="future-flights-empty">No selected upcoming Pegasus flights. Select a flight from today's schedule to see it here.</p>
        ) : (
          <>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hidden md:block">
            <div className="overflow-x-auto max-h-[380px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2.5">Dir</th>
                    <th className="px-3 py-2.5">Date</th>
                    <th className="px-3 py-2.5">Flight</th>
                    <th className="px-3 py-2.5">Route</th>
                    <th className="px-3 py-2.5">Sched</th>
                    <th className="px-3 py-2.5">Reg</th>
                    <th className="px-3 py-2.5">A/C</th>
                    <th className="px-3 py-2.5">Cargo</th>
                    <th className="px-3 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {futureSorted.map((f, i) => (
                    <tr key={f.id || `${f.flight_number}-${f.flight_date}-${i}`} className={`border-t border-slate-100 hover:bg-slate-50 ${f.fixed ? "bg-emerald-50/40" : ""}`} data-testid={`future-flight-row-${f.flight_number}`}>
                      <td className="px-3 py-2.5"><DirBadge dir={f.direction} /></td>
                      <td className="px-3 py-2.5 whitespace-nowrap font-medium text-slate-700">{f.flight_date}</td>
                      <td className="px-3 py-2.5">
                        <span className="font-semibold text-slate-900 inline-flex items-center gap-1.5">{f.flight_number}{f.fixed && <Pin size={12} className="text-emerald-600" />}</span>
                        <span className="text-xs text-slate-400 ml-1">{f.airline}</span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{f.origin || "—"} → {f.destination || "—"}</td>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{f.scheduled_time || "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{f.registration || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{f.aircraft_type || "—"}</td>
                      <td className="px-3 py-2.5">
                        {(f.cargo_count || 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs"><Package size={12} className="text-indigo-600" /> <span className="font-semibold text-indigo-700">{f.cargo_count}</span> · {Math.round(f.cargo_weight || 0).toLocaleString()} kg · {f.cargo_pieces || 0} pcs</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {!f.fixed ? (
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => doFix(f)} data-testid={`future-fix-${f.flight_number}`}>
                              <Pin size={14} className="mr-1" /> Fix
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="h-8" onClick={() => setCargoFlight(f)} data-testid={`future-cargo-${f.flight_number}`}>
                              <Package size={14} className="mr-1" /> Cargo
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="md:hidden space-y-2" data-testid="future-cards">
            {futureSorted.map((f, i) => (
              <div key={f.id || `${f.flight_number}-fmc-${i}`} className={`rounded-xl border p-3 shadow-sm ${f.fixed ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`} data-testid={`future-card-${f.flight_number}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5"><DirBadge dir={f.direction} /><span className="font-bold text-slate-900">{f.flight_number}</span>{f.fixed && <Pin size={12} className="text-emerald-600" />}</div>
                  <span className="text-xs text-slate-500 whitespace-nowrap">{f.flight_date}</span>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 text-sm">
                  <span className="font-medium text-slate-700">{f.origin || "—"}</span><span className="text-slate-300">→</span><span className="font-medium text-slate-700">{f.destination || "—"}</span>
                  <span className="text-slate-400 text-xs ml-auto">{f.scheduled_time || "—"}</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-xs">
                  <span className="text-slate-500"><span className="font-mono">{f.registration || "—"}</span> · {f.aircraft_type || "—"}</span>
                  {(f.cargo_count || 0) > 0 && <span className="inline-flex items-center gap-1 text-indigo-700"><Package size={12} /> {f.cargo_count} · {Math.round(f.cargo_weight || 0).toLocaleString()} kg</span>}
                </div>
                <div className="mt-2.5">
                  {!f.fixed ? (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-9 w-full" onClick={() => doFix(f)} data-testid={`future-fix-${f.flight_number}-m`}><Pin size={14} className="mr-1" /> Fix</Button>
                  ) : (
                    <Button size="sm" variant="outline" className="h-9 w-full" onClick={() => setCargoFlight(f)} data-testid={`future-cargo-${f.flight_number}-m`}><Package size={14} className="mr-1" /> Cargo</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>

      {/* Past fixed flights — click a row to see its cargo */}
      <div className="mt-8" data-testid="past-flights-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base md:text-lg font-semibold text-slate-800 flex items-center gap-2">
            <History size={18} className="text-slate-500" /> Past Flights
            <span className="text-sm font-normal text-slate-400">({pastFlights.length})</span>
          </h2>
          {pastFlights.length > 0 && (
            <Button variant="outline" size="sm" className="h-8" onClick={() => setPastSort((s) => (s === "asc" ? "desc" : "asc"))} data-testid="past-sort-toggle">
              <ArrowDownUp size={14} className="mr-1.5" /> {pastSort === "asc" ? "Oldest → Newest" : "Newest → Oldest"}
            </Button>
          )}
        </div>
        {pastFlights.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="past-flights-empty">No past fixed flights yet.</p>
        ) : (
          <>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hidden md:block">
            <div className="overflow-x-auto max-h-[380px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2.5">Dir</th>
                    <th className="px-3 py-2.5">Date</th>
                    <th className="px-3 py-2.5">Flight</th>
                    <th className="px-3 py-2.5">Route</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Reg</th>
                    <th className="px-3 py-2.5">A/C</th>
                    <th className="px-3 py-2.5">Cargo</th>
                  </tr>
                </thead>
                <tbody>
                  {pastSorted.map((f) => (
                    <tr
                      key={f.id}
                      className="border-t border-slate-100 hover:bg-emerald-50/60 cursor-pointer"
                      onClick={() => setCargoFlight(f)}
                      data-testid={`past-flight-row-${f.flight_number}`}
                      title="Click to see cargo details"
                    >
                      <td className="px-3 py-2.5"><DirBadge dir={f.direction} /></td>
                      <td className="px-3 py-2.5 font-medium text-slate-700 whitespace-nowrap">{f.flight_date}</td>
                      <td className="px-3 py-2.5"><span className="font-semibold text-slate-900">{f.flight_number}</span> <span className="text-xs text-slate-400">{f.airline}</span></td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{f.origin || "—"} → {f.destination || "—"}</td>
                      <td className="px-3 py-2.5"><StatusChip status={f.status} /></td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{f.registration || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{f.aircraft_type || "—"}</td>
                      <td className="px-3 py-2.5">
                        {(f.cargo_count || 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs"><Package size={12} className="text-indigo-600" /> <span className="font-semibold text-indigo-700">{f.cargo_count}</span> · {Math.round(f.cargo_weight || 0).toLocaleString()} kg · {f.cargo_pieces || 0} pcs</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="md:hidden space-y-2" data-testid="past-cards">
            {pastSorted.map((f) => (
              <button key={`${f.id}-pmc`} onClick={() => setCargoFlight(f)} className="w-full text-left rounded-xl border border-slate-200 bg-white p-3 shadow-sm hover:border-emerald-300" data-testid={`past-flight-card-${f.flight_number}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5"><DirBadge dir={f.direction} /><span className="font-bold text-slate-900">{f.flight_number}</span><span className="text-xs text-slate-400">{f.airline}</span></div>
                  <div className="flex items-center gap-1.5"><StatusChip status={f.status} /><span className="text-xs text-slate-500 whitespace-nowrap">{f.flight_date}</span></div>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 text-sm">
                  <span className="font-medium text-slate-700">{f.origin || "—"}</span><span className="text-slate-300">→</span><span className="font-medium text-slate-700">{f.destination || "—"}</span>
                  <span className="text-slate-400 text-xs ml-auto font-mono">{f.registration || "—"}</span>
                </div>
                {(f.cargo_count || 0) > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-indigo-700"><Package size={12} /> <span className="font-semibold">{f.cargo_count}</span><span className="text-slate-500">· {Math.round(f.cargo_weight || 0).toLocaleString()} kg · {f.cargo_pieces || 0} pcs</span></div>
                )}
              </button>
            ))}
          </div>
          </>
        )}
      </div>

      {searchOpen && <SearchModal date={date} onClose={() => setSearchOpen(false)} onAdded={load} existing={flights} />}
      {manualOpen && <ManualModal date={date} onClose={() => setManualOpen(false)} onSaved={() => { load(); loadPast(); loadFuture(); }} />}
      {editFlight && <EditModal flight={editFlight} onClose={() => setEditFlight(null)} onSaved={() => { load(); loadPast(); loadFuture(); }} />}
      {cargoFlight && <CargoModal flight={cargoFlight} entries={entries} onClose={() => setCargoFlight(null)} onSaved={() => { load(); loadPast(); loadFuture(); }} />}
      {historyOpen && <HistoryModal onClose={() => setHistoryOpen(false)} onOpenCargo={(f) => { setHistoryOpen(false); setCargoFlight(f); }} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove flight {deleteTarget?.flight_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This unfixes the flight and clears its cargo booking. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="inflight-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------- Search modal (scope=all) ---------- */
function SearchModal({ date, onClose, onAdded, existing }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const fixedNums = new Set(existing.filter((f) => f.fixed).map((f) => f.flight_number));

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await getCargoFlightSchedule({ date, scope: "all", q: q || undefined });
      setRows(data.flights || []);
      if (!data.scrape_ok) toast.error("Airport schedule unreachable right now");
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const add = async (f) => {
    try {
      await fixCargoFlight({ ...f, flight_date: date, source: "search" });
      toast.success(`Flight ${f.flight_number} fixed`);
      fixedNums.add(f.flight_number);
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add flight");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Search flights — {date}</DialogTitle></DialogHeader>
        <div className="flex gap-2 mb-3">
          <Input
            placeholder="Flight no, airline or city…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            data-testid="search-flight-input"
          />
          <Button onClick={run} disabled={loading} data-testid="search-flight-run">
            <Search size={16} className="mr-1.5" /> Search
          </Button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Dir</th><th className="px-3 py-2">Flight</th>
                <th className="px-3 py-2">Airline</th><th className="px-3 py-2">City</th>
                <th className="px-3 py-2">Sched</th><th className="px-3 py-2 text-right">Add</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No flights.</td></tr>}
              {!loading && rows.map((f, i) => {
                const already = fixedNums.has(f.flight_number);
                return (
                  <tr key={`${f.flight_number}-${i}`} className="border-t border-slate-100">
                    <td className="px-3 py-2"><DirBadge dir={f.direction} /></td>
                    <td className="px-3 py-2 font-semibold">{f.flight_number}</td>
                    <td className="px-3 py-2 text-slate-600">{f.airline}</td>
                    <td className="px-3 py-2 text-slate-600">{f.city_code || f.city}</td>
                    <td className="px-3 py-2">{f.scheduled_time}</td>
                    <td className="px-3 py-2 text-right">
                      {already ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><Check size={14} /> Fixed</span>
                      ) : (
                        <Button size="sm" variant="outline" className="h-8" onClick={() => add(f)} data-testid={`search-add-${f.flight_number}`}>
                          <Plus size={14} className="mr-1" /> Select
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Manual entry modal ---------- */
function ManualModal({ date, onClose, onSaved }) {
  const [form, setForm] = useState(emptyManual);
  const [fdate, setFdate] = useState(date);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!form.flight_number.trim()) { toast.error("Flight number is required"); return; }
    if (!fdate) { toast.error("Flight date is required"); return; }
    try {
      await fixCargoFlight({ ...form, flight_date: fdate, source: "manual" });
      toast.success("Flight added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add flight");
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Manual flight entry</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Flight date * <span className="text-slate-400">(past or future)</span></Label>
            <Input type="date" value={fdate} onChange={(e) => setFdate(e.target.value)} className="mt-1" data-testid="manual-flight_date" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Flight number *</Label>
            <Input value={form.flight_number} onChange={(e) => set("flight_number", e.target.value)} className="mt-1" data-testid="manual-flight_number" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Direction</Label>
            <select value={form.direction} onChange={(e) => set("direction", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="manual-direction">
              <option value="departure">Departure</option>
              <option value="arrival">Arrival</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Registration</Label>
            <Input value={form.registration} onChange={(e) => set("registration", e.target.value)} className="mt-1" data-testid="manual-registration" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Type of aircraft</Label>
            <Input value={form.aircraft_type} onChange={(e) => set("aircraft_type", e.target.value)} className="mt-1" data-testid="manual-aircraft_type" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Origin</Label>
            <Input value={form.origin} onChange={(e) => set("origin", e.target.value)} className="mt-1" data-testid="manual-origin" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Destination</Label>
            <Input value={form.destination} onChange={(e) => set("destination", e.target.value)} className="mt-1" data-testid="manual-destination" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Departing time</Label>
            <Input type="time" value={form.scheduled_time} onChange={(e) => set("scheduled_time", e.target.value)} className="mt-1" data-testid="manual-scheduled_time" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Airline</Label>
            <Input value={form.airline} onChange={(e) => set("airline", e.target.value)} className="mt-1" data-testid="manual-airline" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} data-testid="manual-save">Add flight</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Edit reg/type/status modal ---------- */
function EditModal({ flight, onClose, onSaved }) {
  const [reg, setReg] = useState(flight.registration || "");
  const [type, setType] = useState(flight.aircraft_type || "");
  const [status, setStatus] = useState(flight.status || "");
  const [atd, setAtd] = useState(flight.atd || "");
  const [ata, setAta] = useState(flight.ata || "");
  const [origin, setOrigin] = useState(flight.origin || "");
  const [destination, setDestination] = useState(flight.destination || "");
  const save = async () => {
    try {
      await updateCargoFlight(flight.id, {
        origin: origin.trim().toUpperCase(),
        destination: destination.trim().toUpperCase(),
        registration: reg,
        aircraft_type: type,
        status,
        status_manual: status.trim() !== "",
        atd,
        ata,
      });
      toast.success("Flight updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Could not update flight");
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Edit {flight.flight_number} details</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">Registration</Label>
            <Input value={reg} onChange={(e) => setReg(e.target.value)} className="mt-1" data-testid="edit-registration" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Type of aircraft</Label>
            <Input value={type} onChange={(e) => setType(e.target.value)} className="mt-1" data-testid="edit-aircraft-type" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Origin</Label>
              <Input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="e.g. CRL" className="mt-1" data-testid="edit-origin" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Destination</Label>
              <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g. SAW" className="mt-1" data-testid="edit-destination" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Departure time (ATD)</Label>
              <Input type="time" value={atd} onChange={(e) => setAtd(e.target.value)} className="mt-1" data-testid="edit-atd" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Arrival time (ATA)</Label>
              <Input type="time" value={ata} onChange={(e) => setAta(e.target.value)} className="mt-1" data-testid="edit-ata" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Status override</Label>
            <Input value={status} onChange={(e) => setStatus(e.target.value)} placeholder="e.g. LANDED, BOARDING, DELAYED" className="mt-1" data-testid="edit-status" />
            <p className="text-[11px] text-slate-400 mt-1">Manual values are kept and won't be overwritten by auto-search / live sync.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} data-testid="edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Cargo booking modal (booked view + add + split) ---------- */
function CargoModal({ flight, entries, onClose, onSaved }) {
  const [view, setView] = useState("booked");
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ weight: 0, pieces: 0 });
  const [loading, setLoading] = useState(true);
  const [splitItem, setSplitItem] = useState(null);
  const [otherFlights, setOtherFlights] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [manifestOpen, setManifestOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [manifestData, setManifestData] = useState({});
  const [tagsData, setTagsData] = useState({});

  const loadBooked = useCallback(() => {
    setLoading(true);
    getFlightCargo(flight.id).then((r) => {
      setItems(r.data.items || []);
      setTotals({ weight: r.data.total_weight || 0, pieces: r.data.total_pieces || 0 });
      setManifestData(r.data.manifest_data || {});
      setTagsData(r.data.tags_data || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, [flight.id]);

  useEffect(() => { loadBooked(); }, [loadBooked]);
  useEffect(() => {
    listCargoFlights().then((r) => setOtherFlights((r.data || []).filter((f) => f.id !== flight.id))).catch(() => {});
  }, [flight.id]);

  const refresh = () => { loadBooked(); onSaved && onSaved(); };

  const release = async (entryId) => {
    try { await releaseFlightCargo(flight.id, entryId); toast.success("Cargo released"); refresh(); }
    catch { toast.error("Could not release cargo"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Cargo booked on {flight.flight_number}{flight.flight_date ? ` — ${flight.flight_date}` : ""}</DialogTitle>
        </DialogHeader>

        {view === "booked" ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-slate-600" data-testid="cargo-booked-totals">
                <span className="font-semibold text-slate-900">{items.length}</span> shipment(s) ·{" "}
                <span className="font-semibold text-indigo-700">{Math.round(totals.weight).toLocaleString()} kg</span> ·{" "}
                <span className="font-semibold text-indigo-700">{totals.pieces} pcs</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setManifestOpen(true)} disabled={items.length === 0} data-testid="cargo-manifest-btn" title="Generate an IATA cargo manifest">
                  <FileText size={15} className="mr-1.5" /> Manifest
                </Button>
                <Button variant="outline" onClick={() => setTagsOpen(true)} disabled={items.length === 0} data-testid="cargo-tags-btn" title="Print piece tags with barcode">
                  <Tags size={15} className="mr-1.5" /> Print tags
                </Button>
                <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="cargo-edit-details-btn">
                  <Pencil size={15} className="mr-1.5" /> Edit flight
                </Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setView("add")} data-testid="cargo-add-btn">
                  <Plus size={16} className="mr-1.5" /> Add cargo
                </Button>
              </div>
            </div>
            <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">AWB / Shipment</th>
                    <th className="px-3 py-2">Dir</th>
                    <th className="px-3 py-2">Flight</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Weight</th>
                    <th className="px-3 py-2">Pcs</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
                  {!loading && items.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400" data-testid="cargo-booked-empty">No cargo booked on this flight. Use “Add cargo”.</td></tr>}
                  {!loading && items.map((it) => (
                    <tr key={it.entry_id} className="border-t border-slate-100" data-testid={`booked-row-${it.entry_id}`}>
                      <td className="px-3 py-2 font-semibold text-slate-800">
                        {it.shipment_id || "—"}
                        {it.split && <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold" title="This AWB is split across flights"><GitBranch size={10} /> SPLIT</span>}
                      </td>
                      <td className="px-3 py-2 uppercase text-xs text-slate-500">{it.direction}</td>
                      <td className="px-3 py-2">{it.flight_no || "—"}</td>
                      <td className="px-3 py-2 text-slate-500">{it.shipment_date || "—"}</td>
                      <td className="px-3 py-2">{it.weight_kg} kg{it.split && it.entry_weight ? <span className="text-slate-400 text-xs"> / {it.entry_weight}</span> : null}</td>
                      <td className="px-3 py-2">{it.pieces}{it.split && it.entry_pieces ? <span className="text-slate-400 text-xs"> / {it.entry_pieces}</span> : null}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[220px] truncate" title={it.description || ""}>{it.description || "—"}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setEditEntry(it)} data-testid={`edit-cargo-${it.entry_id}`}>
                            <Pencil size={14} className="mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setSplitItem(it)} data-testid={`split-${it.entry_id}`}>
                            <Split size={14} className="mr-1" /> Split
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-rose-600" onClick={() => release(it.entry_id)} data-testid={`release-${it.entry_id}`}>
                            <Unlink size={14} className="mr-1" /> Release
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="cargo-close">Close</Button>
            </DialogFooter>
          </>
        ) : (
          <AddCargoView
            flight={flight}
            entries={entries}
            bookedIds={items.map((i) => i.entry_id)}
            onBack={() => setView("booked")}
            onAdded={() => { setView("booked"); refresh(); }}
          />
        )}

        {editOpen && (
          <EditModal flight={flight} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); refresh(); }} />
        )}
        {editEntry && (
          <CargoEntryEditDialog entry={editEntry} onClose={() => setEditEntry(null)} onSaved={() => { setEditEntry(null); refresh(); }} />
        )}
        {splitItem && (
          <SplitDialog
            flight={flight}
            item={splitItem}
            otherFlights={otherFlights}
            onClose={() => setSplitItem(null)}
            onDone={() => { setSplitItem(null); refresh(); }}
          />
        )}
        {manifestOpen && (
          <ManifestDialog flight={flight} items={items} savedData={manifestData} onSavedData={setManifestData} onClose={() => setManifestOpen(false)} />
        )}
        {tagsOpen && (
          <TagDialog flight={flight} items={items} savedData={tagsData} onSavedData={setTagsData} onClose={() => setTagsOpen(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- IATA manifest generator ---------- */
const MANIFEST_MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function ManifestDialog({ flight, items, savedData, onSavedData, onClose }) {
  const { user } = useAuth();
  const [manifestNo, setManifestNo] = useState(savedData?.manifestNo || "");
  const [customsRef, setCustomsRef] = useState(savedData?.customsRef || "");
  const [rowMeta, setRowMeta] = useState(savedData?.rowMeta || {});
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const setMeta = (id, k, v) => setRowMeta((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { manifestNo, customsRef, rowMeta };
      const { data } = await saveCargoManifestData(flight.id || flight.flight_number, payload);
      onSavedData && onSavedData(data.manifest_data || payload);
      toast.success("Manifest details saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save manifest details");
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    const now = new Date();
    const p2 = (n) => String(n).padStart(2, "0");
    const meta = {
      preparedBy: user?.full_name || "",
      manifestNo,
      customsRef,
      datePrepared: `${p2(now.getDate())}-${p2(now.getMonth() + 1)}-${now.getFullYear()}`,
      dateLong: `${now.getDate()} ${MANIFEST_MONTHS_LONG[now.getMonth()]} ${now.getFullYear()}`,
      timeStr: `${p2(now.getHours())}:${p2(now.getMinutes())}:${p2(now.getSeconds())}`,
      rowMeta,
    };
    setGenerating(true);
    try {
      const { data } = await generateCargoManifest(flight.id || flight.flight_number, { flight, items, meta });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const fnNo = (flight.flight_number || "manifest").replace(/\s+/g, "");
      a.download = `Manifest_${fnNo}_${flight.flight_date || ""}.pdf`.replace(/_+\.pdf$/, ".pdf");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Manifest PDF downloaded");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the manifest PDF");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Generate manifest — {flight.flight_number}{flight.flight_date ? ` · ${flight.flight_date}` : ""}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Prepared by <span className="font-medium text-slate-700">{user?.full_name || "—"}</span> · date &amp; time are added automatically. Fields below are optional.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Manifest No</Label>
            <Input value={manifestNo} onChange={(e) => setManifestNo(e.target.value)} className="mt-1" data-testid="manifest-no-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Customs Ref</Label>
            <Input value={customsRef} onChange={(e) => setCustomsRef(e.target.value)} className="mt-1" data-testid="manifest-customs-input" />
          </div>
        </div>
        <div className="max-h-[45vh] overflow-y-auto rounded-lg border border-slate-200 mt-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">AWB</th>
                <th className="px-3 py-2">Pcs</th>
                <th className="px-3 py-2">Kg</th>
                <th className="px-3 py-2">Operator P</th>
                <th className="px-3 py-2">Official use</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.entry_id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono font-semibold text-slate-800">{it.shipment_id || "—"}</td>
                  <td className="px-3 py-2">{it.pieces}</td>
                  <td className="px-3 py-2">{it.weight_kg}</td>
                  <td className="px-2 py-1.5">
                    <Input value={rowMeta[it.entry_id]?.operatorP || ""} onChange={(e) => setMeta(it.entry_id, "operatorP", e.target.value)} className="h-8" placeholder="BRU-AMM" data-testid={`manifest-operp-${it.entry_id}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input value={rowMeta[it.entry_id]?.officialUse || ""} onChange={(e) => setMeta(it.entry_id, "officialUse", e.target.value)} className="h-8" placeholder="T1 - EAW SPX" data-testid={`manifest-official-${it.entry_id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="manifest-cancel">Cancel</Button>
          <Button variant="outline" onClick={save} disabled={saving} className="text-indigo-700 border-indigo-200 hover:bg-indigo-50" data-testid="manifest-save">
            <Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={generate} disabled={generating} data-testid="manifest-generate">
            <FileText size={15} className="mr-1.5" /> {generating ? "Generating…" : "Download manifest PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Cargo piece tags (barcode, A5 ×2 per A4) ---------- */
function TagDialog({ flight, items, savedData, onSavedData, onClose }) {
  const savedById = {};
  (savedData?.groups || []).forEach((g) => { if (g.entry_id) savedById[g.entry_id] = g; });
  const mkDims = (n, src) => {
    const arr = (src || []).slice(0, n).map((d) => ({ l: String(d?.l ?? ""), w: String(d?.w ?? ""), h: String(d?.h ?? "") }));
    while (arr.length < n) arr.push({ l: "", w: "", h: "" });
    return arr;
  };
  const [groups, setGroups] = useState(() => items.map((it) => {
    const sv = savedById[it.entry_id];
    const n = Math.max(1, sv ? (parseInt(sv.pcs, 10) || (sv.weights?.length ?? it.pieces) || 1) : (it.pieces || 1));
    const weights = (sv?.weights || []).slice(0, n).map((x) => String(x ?? ""));
    while (weights.length < n) weights.push("");
    return {
      entry_id: it.entry_id,
      awb: it.shipment_id || "",
      pcs: String(n),
      hintPcs: it.pieces || 0,
      hintKg: Math.round(it.weight_kg || 0),
      checked: true,
      weights,
      dims: mkDims(n, sv?.dims),
    };
  }));
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const setGroup = (idx, patch) => setGroups((p) => p.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  const setPcs = (idx, v) => {
    const n = Math.max(0, Math.min(200, parseInt(v, 10) || 0));
    setGroups((p) => p.map((g, i) => {
      if (i !== idx) return g;
      const nextW = g.weights.slice(0, n);
      while (nextW.length < n) nextW.push("");
      const nextD = g.dims.slice(0, n);
      while (nextD.length < n) nextD.push({ l: "", w: "", h: "" });
      return { ...g, pcs: v, weights: nextW, dims: nextD };
    }));
  };
  const setW = (gi, wi, v) => setGroups((p) => p.map((g, i) => (i === gi ? { ...g, weights: g.weights.map((x, j) => (j === wi ? v : x)) } : g)));
  const setDim = (gi, di, k, v) => setGroups((p) => p.map((g, i) => (i === gi ? { ...g, dims: g.dims.map((d, j) => (j === di ? { ...d, [k]: v } : d)) } : g)));
  const toggle = (idx) => setGroups((p) => p.map((g, i) => (i === idx ? { ...g, checked: !g.checked } : g)));

  const volOf = (d) => {
    const l = parseFloat(d?.l), w = parseFloat(d?.w), h = parseFloat(d?.h);
    if (l > 0 && w > 0 && h > 0) return (l * w * h) / 1_000_000;
    return 0;
  };

  const activeCount = (g) => (g.checked ? g.weights.length : 0);
  const total = groups.reduce((s, g) => s + activeCount(g), 0);
  const totalVol = groups.reduce((s, g) => s + (g.checked ? g.dims.reduce((a, d) => a + volOf(d), 0) : 0), 0);
  // running start index for continuous numbering across ticked AWBs
  let run = 0;
  const starts = groups.map((g) => { const s = run; if (g.checked) run += g.weights.length; return s; });

  const buildGroupsPayload = (src) => src.map((g) => ({
    entry_id: g.entry_id,
    awb: g.awb.trim().toUpperCase(),
    pcs: Number(g.pcs) || g.weights.length,
    weights: g.weights.map((w) => (w === "" ? 0 : Number(w))),
    dims: g.dims.map((d) => ({ l: Number(d.l) || 0, w: Number(d.w) || 0, h: Number(d.h) || 0 })),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { groups: buildGroupsPayload(groups) };
      const { data } = await saveCargoTagsData(flight.id || flight.flight_number, payload);
      onSavedData && onSavedData(data.tags_data || payload);
      toast.success("Package weights & sizes saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save tags");
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    const chosen = groups.filter((g) => g.checked && g.weights.length > 0 && g.awb.trim());
    if (chosen.length === 0) { toast.error("Tick at least one AWB with pieces"); return; }
    setGenerating(true);
    try {
      const payload = {
        flight: {
          flight_number: flight.flight_number,
          registration: flight.registration,
          origin: flight.origin,
          destination: flight.destination,
          direction: flight.direction,
          flight_date: flight.flight_date,
          scheduled_time: flight.scheduled_time,
          estimated_time: flight.estimated_time,
        },
        groups: buildGroupsPayload(chosen),
      };
      const { data } = await generateCargoTags(flight.id || flight.flight_number, payload);
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Tags_${(flight.flight_number || "").replace(/\s+/g, "")}_${total}pcs.pdf`.replace(/_+/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Tags PDF downloaded");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the tags PDF");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Print piece tags — {flight.flight_number}{flight.flight_date ? ` · ${flight.flight_date}` : ""}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Two A5 tags per A4 page. Tick the AWBs to print and set each one's pieces, scale weight and size — pieces are numbered continuously across all ticked AWBs (total <span className="font-semibold text-slate-700" data-testid="tag-total">{total}</span>{totalVol > 0 ? <> · <span className="font-semibold text-slate-700" data-testid="tag-total-vol">{totalVol.toFixed(3)} m³</span></> : null}).</p>
        <div className="max-h-[55vh] overflow-y-auto space-y-2 mt-1">
          {groups.map((g, gi) => (
            <div key={g.entry_id} className={`rounded-lg border p-3 ${g.checked ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200 bg-white"}`} data-testid={`tag-awb-group-${g.awb}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <input type="checkbox" checked={g.checked} onChange={() => toggle(gi)} className="w-4 h-4 accent-emerald-600" data-testid={`tag-awb-check-${g.awb}`} />
                <span className="font-mono font-semibold text-slate-900">{g.awb || "—"}</span>
                <span className="text-xs text-slate-400">{g.hintPcs} pcs · {g.hintKg} kg</span>
                <div className="ml-auto flex items-center gap-1.5">
                  <Label className="text-xs text-slate-500">Pieces</Label>
                  <Input type="number" min="1" value={g.pcs} onChange={(e) => setPcs(gi, e.target.value)} disabled={!g.checked} className="h-8 w-20" data-testid={`tag-count-${g.awb}`} />
                </div>
              </div>
              {g.checked && g.weights.length > 0 && (
                <div className="mt-2 space-y-1.5" data-testid={`tag-weights-${g.awb}`}>
                  {g.weights.map((w, wi) => {
                    const v = volOf(g.dims[wi]);
                    return (
                      <div key={wi} className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                        <span className="text-xs font-semibold text-slate-600 w-12 tabular-nums">{starts[gi] + wi + 1}/{total}</span>
                        <Input type="number" step="0.1" value={w} onChange={(e) => setW(gi, wi, e.target.value)} placeholder="kg" className="h-8 w-20" data-testid={`tag-weight-${g.awb}-${wi}`} />
                        <div className="flex items-center gap-1 text-slate-400">
                          <Input type="number" step="1" value={g.dims[wi].l} onChange={(e) => setDim(gi, wi, "l", e.target.value)} placeholder="L" className="h-8 w-16" data-testid={`tag-dim-l-${g.awb}-${wi}`} />
                          <X size={11} />
                          <Input type="number" step="1" value={g.dims[wi].w} onChange={(e) => setDim(gi, wi, "w", e.target.value)} placeholder="W" className="h-8 w-16" data-testid={`tag-dim-w-${g.awb}-${wi}`} />
                          <X size={11} />
                          <Input type="number" step="1" value={g.dims[wi].h} onChange={(e) => setDim(gi, wi, "h", e.target.value)} placeholder="H" className="h-8 w-16" data-testid={`tag-dim-h-${g.awb}-${wi}`} />
                          <span className="text-[10px] text-slate-400">cm</span>
                        </div>
                        <span className="text-xs tabular-nums w-24 text-right text-slate-500" data-testid={`tag-vol-${g.awb}-${wi}`}>{v > 0 ? `${v.toFixed(3)} m³` : "—"}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="tag-cancel">Cancel</Button>
          <Button variant="outline" onClick={save} disabled={saving} className="text-indigo-700 border-indigo-200 hover:bg-indigo-50" data-testid="tag-save">
            <Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={generate} disabled={generating || total === 0} data-testid="tag-generate">
            <Tags size={15} className="mr-1.5" /> {generating ? "Generating…" : `Download tags PDF (${total})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Edit a booked cargo entry (AWB details) ---------- */
function CargoEntryEditDialog({ entry, onClose, onSaved }) {
  const [form, setForm] = useState({
    shipment_id: entry.shipment_id || "",
    weight_kg: entry.entry_weight ?? entry.weight_kg ?? "",
    pieces: entry.entry_pieces ?? entry.pieces ?? "",
    description: entry.description || "",
    operator: entry.operator || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await updateCargoEntry(entry.entry_id, {
        shipment_id: form.shipment_id,
        weight_kg: form.weight_kg === "" ? null : Number(form.weight_kg),
        pieces: form.pieces === "" ? null : Number(form.pieces),
        description: form.description,
        operator: form.operator,
      });
      toast.success("Cargo updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update cargo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Edit cargo — {entry.shipment_id || "AWB"}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Changes apply to the shipment's own totals. Split allocations are kept as-is.</p>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs text-slate-500">AWB / Shipment</Label>
            <Input value={form.shipment_id} onChange={(e) => set("shipment_id", e.target.value)} className="mt-1" data-testid="edit-cargo-awb" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Weight (kg)</Label>
              <Input type="number" step="0.1" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className="mt-1" data-testid="edit-cargo-weight" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Pieces</Label>
              <Input type="number" value={form.pieces} onChange={(e) => set("pieces", e.target.value)} className="mt-1" data-testid="edit-cargo-pieces" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Operator</Label>
            <Input value={form.operator} onChange={(e) => set("operator", e.target.value)} className="mt-1" data-testid="edit-cargo-operator" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Description</Label>
            <Input value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1" data-testid="edit-cargo-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-cargo-cancel">Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={saving} data-testid="edit-cargo-save">
            <Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save cargo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Add cargo (pool picker) ---------- */
function AddCargoView({ flight, entries, bookedIds, onBack, onAdded }) {
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);
  const [byEntry, setByEntry] = useState({});
  useEffect(() => { getCargoFlightAssignments().then((r) => setByEntry(r.data?.by_entry || {})).catch(() => {}); }, []);
  const bookedSet = new Set(bookedIds);
  const otherFlights = (id) => (byEntry[id] || []).filter((a) => a.flight_id !== flight.id);
  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const filtered = entries.filter((e) => {
    if (bookedSet.has(e.id)) return false;
    if (q) { const s = q.toLowerCase(); const hit = (e.shipment_id || "").toLowerCase().includes(s) || (e.flight_no || "").toLowerCase().includes(s) || (e.description || "").toLowerCase().includes(s); if (!hit) return false; }
    if (onlyUnassigned) return otherFlights(e.id).length === 0;
    return true;
  });
  const add = async () => {
    try {
      await setCargoFlightEntries(flight.id, [...bookedIds, ...Array.from(selected)]);
      toast.success(`Added ${selected.size} shipment(s)`);
      onAdded();
    } catch { toast.error("Could not add cargo"); }
  };
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="cargo-add-back"><ArrowLeft size={15} /> Back to booked</button>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none"><input type="checkbox" checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)} className="w-4 h-4 accent-emerald-600" data-testid="cargo-picker-only-unassigned" /> Only non-loaded</label>
          <span className="text-sm text-slate-500">{selected.size} selected</span>
        </div>
      </div>
      <Input placeholder="Search AWB, flight, description…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cargo-picker-search" className="mb-2" />
      <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 w-10"></th>
              <th className="px-3 py-2">AWB / Shipment</th>
              <th className="px-3 py-2">Dir</th>
              <th className="px-3 py-2">Flight</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Weight</th>
              <th className="px-3 py-2">Pcs</th>
              <th className="px-3 py-2">Loaded on</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">No cargo available.</td></tr>}
            {filtered.map((e) => {
              const on = selected.has(e.id);
              const others = otherFlights(e.id);
              return (
                <tr key={e.id} className={`border-t border-slate-100 cursor-pointer ${on ? "bg-emerald-50" : "hover:bg-slate-50"}`} onClick={() => toggle(e.id)} data-testid={`cargo-pick-row-${e.id}`}>
                  <td className="px-3 py-2"><input type="checkbox" checked={on} readOnly className="w-4 h-4 accent-emerald-600" data-testid={`cargo-pick-${e.id}`} /></td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{e.shipment_id || "—"}</td>
                  <td className="px-3 py-2 uppercase text-xs text-slate-500">{e.direction}</td>
                  <td className="px-3 py-2">{e.flight_no || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{e.shipment_date || "—"}</td>
                  <td className="px-3 py-2">{e.weight_kg != null ? `${e.weight_kg} kg` : "—"}</td>
                  <td className="px-3 py-2">{e.pieces ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">{others.length === 0 ? <span className="text-slate-300">—</span> : <span className="text-indigo-600">{others.map((a) => `${a.flight_number} @ ${a.flight_date}`).join(", ")}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onBack}>Cancel</Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={add} disabled={selected.size === 0} data-testid="cargo-add-save">Add selected ({selected.size})</Button>
      </DialogFooter>
    </>
  );
}

/* ---------- Split cargo dialog ---------- */
function SplitDialog({ flight, item, otherFlights, onClose, onDone }) {
  const [mode, setMode] = useState("existing");
  const [targetId, setTargetId] = useState("");
  const [newNum, setNewNum] = useState("");
  const [newDate, setNewDate] = useState(flight.flight_date || todayISO());
  const [pieces, setPieces] = useState("");
  const [weight, setWeight] = useState("");
  const remP = item.pieces - (parseInt(pieces, 10) || 0);
  const remW = Math.round((item.weight_kg - (parseFloat(weight) || 0)) * 10) / 10;
  const save = async () => {
    const p = parseInt(pieces, 10) || 0;
    const w = parseFloat(weight) || 0;
    if (p <= 0 || w <= 0) { toast.error("Enter pieces and weight to move"); return; }
    if (p > item.pieces || w > item.weight_kg) { toast.error(`Cannot move more than booked (${item.pieces} pcs / ${item.weight_kg} kg)`); return; }
    const payload = { entry_id: item.entry_id, pieces: p, weight_kg: w };
    if (mode === "existing") { if (!targetId) { toast.error("Choose a target flight"); return; } payload.target_flight_id = targetId; }
    else { if (!newNum || !newDate) { toast.error("Enter target flight number and date"); return; } payload.flight_number = newNum; payload.flight_date = newDate; payload.direction = flight.direction; }
    try { await splitFlightCargo(flight.id, payload); toast.success("Cargo split — total kept exact"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not split cargo"); }
  };
  const pill = (active) => `px-3 py-1.5 text-xs font-semibold rounded-md ${active ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Split {item.shipment_id}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Booked on {flight.flight_number}: <b>{item.pieces} pcs / {item.weight_kg} kg</b>. Enter the portion to move to another flight — the rest stays here and the AWB total stays exact.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-500">Pieces to move</Label><Input type="number" min="1" max={item.pieces} value={pieces} onChange={(e) => setPieces(e.target.value)} className="mt-1" data-testid="split-pieces" /></div>
          <div><Label className="text-xs text-slate-500">Weight to move (kg)</Label><Input type="number" min="0" step="0.1" max={item.weight_kg} value={weight} onChange={(e) => setWeight(e.target.value)} className="mt-1" data-testid="split-weight" /></div>
        </div>
        <div className="text-xs text-slate-500">Stays on {flight.flight_number}: <b>{remP < 0 ? 0 : remP} pcs / {remW < 0 ? 0 : remW} kg</b></div>
        <div className="flex gap-2">
          <button onClick={() => setMode("existing")} className={pill(mode === "existing")} data-testid="split-mode-existing">Existing flight</button>
          <button onClick={() => setMode("new")} className={pill(mode === "new")} data-testid="split-mode-new">New flight</button>
        </div>
        {mode === "existing" ? (
          <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="split-target-select">
            <option value="">Choose target flight…</option>
            {otherFlights.map((f) => <option key={f.id} value={f.id}>{f.flight_number} @ {f.flight_date} ({f.origin || "?"}→{f.destination || "?"})</option>)}
          </select>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Flight number</Label><Input value={newNum} onChange={(e) => setNewNum(e.target.value)} className="mt-1" data-testid="split-new-number" /></div>
            <div><Label className="text-xs text-slate-500">Date</Label><Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="mt-1" data-testid="split-new-date" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} data-testid="split-save">Split cargo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Fixed-flight history / archive modal ---------- */
function HistoryModal({ onClose, onOpenCargo }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | needs | complete
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);

  const loadRows = () => {
    setLoading(true);
    listCargoFlights().then((r) => {
      const list = (r.data || []).slice().sort((a, b) => (b.flight_date || "").localeCompare(a.flight_date || "") || (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
      setRows(list);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { loadRows(); }, []);

  const needsData = (f) => !f.registration || !f.aircraft_type;
  const filtered = rows.filter((f) => {
    if (q) {
      const s = q.toLowerCase();
      const hit = (f.flight_number || "").toLowerCase().includes(s)
        || (f.flight_date || "").includes(s)
        || (f.registration || "").toLowerCase().includes(s)
        || (f.destination || "").toLowerCase().includes(s)
        || (f.origin || "").toLowerCase().includes(s);
      if (!hit) return false;
    }
    if (filter === "needs") return needsData(f);
    if (filter === "complete") return !needsData(f);
    return true;
  });

  const fill = async () => {
    const targets = filtered.filter(needsData).map((f) => f.id);
    if (!targets.length) { toast.info("Nothing to fill in this view"); return; }
    setEnriching(true);
    try {
      const { data } = await enrichCargoFlights({ flight_ids: targets, only_missing: true });
      toast.success(`Filled ${data.enriched} of ${data.total} flight(s). Older flights often can't be found online.`);
      loadRows();
    } catch (e) {
      toast.error("Could not fetch flight data from the net");
    } finally {
      setEnriching(false);
    }
  };

  const FilterBtn = ({ value, label }) => (
    <button
      onClick={() => setFilter(value)}
      className={`px-3 py-1.5 text-xs font-semibold rounded-md ${filter === value ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
      data-testid={`history-filter-${value}`}
    >{label}</button>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader><DialogTitle>Flight archive — all fixed flights</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <Input placeholder="Search flight, date, registration, city…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="history-search" className="max-w-xs" />
          <div className="flex items-center gap-2">
            <FilterBtn value="all" label={`All (${rows.length})`} />
            <FilterBtn value="needs" label="To be filled" />
            <FilterBtn value="complete" label="Complete" />
            <Button size="sm" variant="outline" onClick={fill} disabled={enriching} data-testid="history-fill-btn" title="Look up registration / type / status / times online for flights missing data">
              <RefreshCw size={14} className={`mr-1.5 ${enriching ? "animate-spin" : ""}`} /> Fill from web
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mb-2">Fill pulls registration &amp; aircraft type from live tracking and status/actual times from the airport schedule. Flights more than a few days old usually can't be found online.</p>
        <div className="max-h-[58vh] overflow-y-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Flight</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Times</th>
                <th className="px-3 py-2">Reg</th>
                <th className="px-3 py-2">A/C</th>
                <th className="px-3 py-2">Cargo</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400" data-testid="history-empty">No matching flights.</td></tr>}
              {!loading && filtered.map((f) => (
                <tr key={f.id} className="border-t border-slate-100 hover:bg-emerald-50/60 cursor-pointer" onClick={() => onOpenCargo(f)} data-testid={`history-row-${f.flight_number}`} title="Click to see cargo details">
                  <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap">{f.flight_date}</td>
                  <td className="px-3 py-2 font-semibold text-slate-900">{f.flight_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{f.origin || "—"} → {f.destination || "—"}</td>
                  <td className="px-3 py-2"><StatusChip status={f.status} /></td>
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                    {f.atd ? `ATD ${f.atd}` : f.ata ? `ATA ${f.ata}` : "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-700">{f.registration || <span className="text-rose-400">—</span>}</td>
                  <td className="px-3 py-2 text-slate-600">{f.aircraft_type || <span className="text-rose-400">—</span>}</td>
                  <td className="px-3 py-2">
                    {(f.cargo_count || 0) > 0 ? (
                      <span className="text-xs"><span className="font-semibold text-indigo-700">{f.cargo_count}</span> · {Math.round(f.cargo_weight || 0).toLocaleString()} kg · {f.cargo_pieces || 0} pcs</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" className="h-7" onClick={(e) => { e.stopPropagation(); onOpenCargo(f); }} data-testid={`history-cargo-${f.flight_number}`}>
                      <Package size={13} className="mr-1" /> Cargo
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
